import React, { useRef } from 'react';
import './SelectInput.scss';
import ReactSelect, {
  Props as ReactSelectProps,
  GroupBase,
  StylesConfig,
  ActionMeta,
  OnChangeValue,
} from 'react-select';
import _ from 'lodash';

export type { GroupBase, StylesConfig, ActionMeta, OnChangeValue };
export { components as selectComponents, createFilter } from 'react-select';

export interface SelectInputProps<
  Option = Record<string, unknown>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends Omit<ReactSelectProps<Option, IsMulti, Group>, 'onInputChange'> {
  // Debounces onInputChange internally — useful for async search components
  debounceMs?: number;
  // Shorthand to set the dropdown z-index without writing a full styles override
  menuZIndex?: number;
  // Sets width of both the control (input) and the menu (dropdown). Defaults to 280px.
  width?: number | string;
  // Raw onInputChange — debounced if debounceMs is set
  onInputChange?: (value: string, actionMeta: { action: string }) => void;
}

function SelectInputInner<
  Option = Record<string, unknown>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: SelectInputProps<Option, IsMulti, Group>, ref: React.Ref<unknown>) {
  const {
    debounceMs,
    menuZIndex,
    width = 280,
    onInputChange,
    styles,
    openMenuOnFocus = true,
    ...rest
  } = props;

  // Stable debounced reference — recreating on every render breaks debounce
  const debouncedInputChange = useRef(
    debounceMs && onInputChange
      ? _.debounce(onInputChange, debounceMs)
      : onInputChange,
  ).current;

  const FONT = "'Source Sans Pro', sans-serif";
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;

  const mergedStyles: StylesConfig<Option, IsMulti, Group> = {
    ...styles,
    control: (base, state) => ({
      ...base,
      width: resolvedWidth,
      ...(typeof styles?.control === 'function'
        ? styles.control(base, state)
        : {}),
    }),
    option: (base, state) => ({
      ...base,
      fontFamily: FONT,
      ...(typeof styles?.option === 'function'
        ? styles.option(base, state)
        : {}),
    }),
    placeholder: (base, state) => ({
      ...base,
      textAlign: 'left',
      fontFamily: FONT,
      fontweight: 600,
      ...(typeof styles?.placeholder === 'function'
        ? styles.placeholder(base, state)
        : {}),
    }),
    singleValue: (base, state) => ({
      ...base,
      fontFamily: FONT,
      ...(typeof styles?.singleValue === 'function'
        ? styles.singleValue(base, state)
        : {}),
    }),
    multiValueLabel: (base, state) => ({
      ...base,
      fontFamily: FONT,
      ...(typeof styles?.multiValueLabel === 'function'
        ? styles.multiValueLabel(base, state)
        : {}),
    }),
    input: (base, state) => ({
      ...base,
      fontFamily: FONT,
      ...(typeof styles?.input === 'function' ? styles.input(base, state) : {}),
    }),
    menu: (base, state) => ({
      ...base,
      width: resolvedWidth,
      textAlign: 'left',
      fontFamily: FONT,
      ...(menuZIndex !== undefined ? { zIndex: menuZIndex } : {}),
      ...(typeof styles?.menu === 'function' ? styles.menu(base, state) : {}),
    }),
    groupHeading: (base, state) => ({
      ...base,
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      ...(typeof styles?.groupHeading === 'function'
        ? styles.groupHeading(base, state)
        : {}),
    }),
  };

  return (
    <ReactSelect<Option, IsMulti, Group>
      ref={ref as React.Ref<never>}
      classNamePrefix="select-input"
      openMenuOnFocus={openMenuOnFocus}
      {...rest}
      styles={mergedStyles}
      onInputChange={
        debouncedInputChange
          ? (value, actionMeta) =>
              debouncedInputChange(value, { action: actionMeta.action })
          : undefined
      }
    />
  );
}

// forwardRef loses generics in TypeScript — re-cast to restore them
export const SelectInput = React.forwardRef(SelectInputInner) as <
  Option = Record<string, unknown>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectInputProps<Option, IsMulti, Group> & {
    ref?: React.Ref<unknown>;
  },
) => React.ReactElement;
